import type { CharacterGridSpec } from "@/character/grid";
import { normalizeGridSpec } from "@/character/grid";
import { normalizeGridTuning, type GridTuning } from "@/character/draft";
import {
  HOME_GRID_COLS,
  HOME_GRID_LEGACY_SIZES,
  HOME_GRID_ROWS,
  HOME_PART_CELLS_MAX,
  HOME_PART_CELLS_MIN,
  clampGridCols,
  clampGridRows,
} from "@/home/grid";
import { LEGACY_SYSTEM_ROOM_URLS } from "@/home/home-assets";
import {
  isStorageQuotaError,
  loadHomeRoomFromIdb,
  saveHomeRoomToIdb,
} from "@/home/home-idb";
import { normalizeImageDataUrl } from "@/home/image-data-url";
import {
  kindPreset,
  normalizePartKind,
  resolvePartFlags,
  type HomePartKind,
} from "@/home/part-kind";
import type { PartSocket } from "@/home/sockets";
import { themeById, type HomeThemeId } from "@/home/themes";
import { normalizeZones, type HomeZone } from "@/home/zones";

const STORAGE_PREFIX = "mind-survival/home/v1";
const HOME_DATA_VERSION = 2 as const;

/** Drop retired system room images so theme gradients apply. */
function migrateThemeBackdropUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (LEGACY_SYSTEM_ROOM_URLS.has(url)) return null;
  return url;
}

export interface HomePartDefinition {
  id: string;
  name: string;
  imageDataUrl: string;
  cellsW: number;
  cellsH: number;
  /** Collision footprint; defaults to cellsW/H when omitted. */
  footprintW: number;
  footprintH: number;
  createdAt: number;
  kind: HomePartKind;
  walkable: boolean;
  stackable: boolean;
  occupies: boolean;
  depthBias: number;
  /** Visual scale inside grid footprint (1 = default). */
  placeScale: number;
  tags: string[];
  /** Soft passage through solid (doorway). */
  passage: boolean;
  /** Allowed grid bands: floor / wall / sky. */
  zones: HomeZone[];
  /** Optional custom sockets; solids get auto front sockets when empty. */
  sockets: PartSocket[];
  /** Monster combat (admin-tuned per part). */
  monsterLevel?: number;
  monsterBaseHp?: number;
  monsterBaseAttack?: number;
  monsterBaseDefense?: number;
  monsterBaseDodge?: number;
  monsterXpReward?: number;
  spritesheetDataUrl?: string;
  grid?: CharacterGridSpec;
  sourceSheetDataUrl?: string;
  gridTuning?: GridTuning;
}

export interface HomePlacement {
  id: string;
  partId: string;
  gridX: number;
  gridY: number;
  /** Per-instance layer bias; overrides part default when set. */
  depthBias?: number;
  /** Per-instance visual scale; falls back to part.placeScale when omitted. */
  placeScale?: number;
  /** Mirror sprite horizontally (visual only). */
  flipX?: boolean;
  /** Mirror sprite vertically (visual only). */
  flipY?: boolean;
  /** Clockwise rotation in 90° steps (visual only). */
  rotation?: number;
}

export interface HomeRoomData {
  version: typeof HOME_DATA_VERSION;
  gridCols: number;
  gridRows: number;
  parts: HomePartDefinition[];
  placements: HomePlacement[];
  /** Built-in theme id. */
  themeId: HomeThemeId | string;
  /** Custom backdrop from a backdrop-kind part. */
  backdropPartId: string | null;
  /** Explicit backdrop URL (theme default or override). */
  backdropUrl: string | null;
  /** Outer margin backdrop from a shell-kind part (Phaser + stage wrap). */
  shellPartId: string | null;
  /** Optional floor-skin part (full-room floor image). */
  floorPartId: string | null;
}

type LegacyHomeRoom = {
  version?: number;
  gridCols?: number;
  gridRows?: number;
  parts?: unknown[];
  placements?: unknown[];
  themeId?: string;
  backdropPartId?: string | null;
  backdropUrl?: string | null;
  shellPartId?: string | null;
  floorPartId?: string | null;
};

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function createDefaultHomeRoom(): HomeRoomData {
  const theme = themeById("day");
  return {
    version: HOME_DATA_VERSION,
    gridCols: HOME_GRID_COLS,
    gridRows: HOME_GRID_ROWS,
    parts: [],
    placements: [],
    themeId: theme.id,
    backdropPartId: null,
    backdropUrl: null,
    shellPartId: null,
    floorPartId: null,
  };
}

export function createPartDefinition(input: {
  id: string;
  name: string;
  imageDataUrl: string;
  cellsW: number;
  cellsH: number;
  kind?: HomePartKind;
  footprintW?: number;
  footprintH?: number;
  tags?: string[];
  depthBias?: number;
  placeScale?: number;
  passage?: boolean;
  zones?: HomeZone[];
  sockets?: PartSocket[];
  createdAt?: number;
  monsterLevel?: number;
  monsterBaseHp?: number;
  monsterBaseAttack?: number;
  monsterBaseDefense?: number;
  monsterBaseDodge?: number;
  monsterXpReward?: number;
  spritesheetDataUrl?: string;
  grid?: CharacterGridSpec;
  sourceSheetDataUrl?: string;
  gridTuning?: GridTuning;
}): HomePartDefinition {
  const kind = normalizePartKind(input.kind);
  const flags = resolvePartFlags(kind);
  const cellsW = clampPartCells(input.cellsW);
  const cellsH = clampPartCells(input.cellsH);
  const footprintW = clampPartCells(input.footprintW ?? cellsW);
  const footprintH = clampPartCells(input.footprintH ?? cellsH);
  const sockets = Array.isArray(input.sockets)
    ? input.sockets.filter(
        (s) =>
          s &&
          typeof s.id === "string" &&
          Number.isInteger(s.dx) &&
          Number.isInteger(s.dy) &&
          Array.isArray(s.accept),
      )
    : [];
  return {
    id: input.id,
    name: input.name,
    imageDataUrl: normalizeImageDataUrl(input.imageDataUrl),
    cellsW,
    cellsH,
    footprintW: Math.min(footprintW, cellsW),
    footprintH: Math.min(footprintH, cellsH),
    createdAt: input.createdAt ?? Date.now(),
    kind,
    walkable: flags.walkable,
    stackable: flags.stackable,
    occupies: flags.occupies,
    depthBias: input.depthBias ?? 0,
    placeScale: clampPlaceScale(input.placeScale ?? 1),
    tags: Array.isArray(input.tags)
      ? input.tags.filter((t) => typeof t === "string").slice(0, 8)
      : [],
    passage: Boolean(input.passage),
    zones: normalizeZones(input.zones, kind),
    sockets,
    monsterLevel: clampMonsterStat(input.monsterLevel, 0, 100),
    monsterBaseHp: clampMonsterStat(input.monsterBaseHp, 1, 1_000_000),
    monsterBaseAttack: clampMonsterStat(input.monsterBaseAttack, 1, 100_000),
    monsterBaseDefense: clampMonsterStat(input.monsterBaseDefense, 0, 100_000),
    monsterBaseDodge: clampMonsterStat(input.monsterBaseDodge, 0, 100_000),
    monsterXpReward: clampMonsterStat(input.monsterXpReward, 0, 10_000_000),
    spritesheetDataUrl:
      typeof input.spritesheetDataUrl === "string" &&
      input.spritesheetDataUrl.startsWith("data:")
        ? normalizeImageDataUrl(input.spritesheetDataUrl)
        : undefined,
    grid: input.grid ? normalizeGridSpec(input.grid) : undefined,
    sourceSheetDataUrl:
      typeof input.sourceSheetDataUrl === "string" &&
      input.sourceSheetDataUrl.startsWith("data:")
        ? normalizeImageDataUrl(input.sourceSheetDataUrl)
        : undefined,
    gridTuning: normalizePartGridTuning(input.gridTuning),
  };
}

function normalizePartGridTuning(raw: unknown): GridTuning | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const g = raw as Partial<GridTuning> & { offset?: Partial<GridTuning["offset"]> };
  return normalizeGridTuning({
    lockTemplateGrid: Boolean(g.lockTemplateGrid),
    frameSize:
      typeof g.frameSize === "number" && Number.isFinite(g.frameSize)
        ? Math.round(g.frameSize)
        : null,
    offset: {
      x: typeof g.offset?.x === "number" ? g.offset.x : 0,
      y: typeof g.offset?.y === "number" ? g.offset.y : 0,
    },
    rowSplit: Array.isArray(g.rowSplit) ? g.rowSplit.map(Boolean) : undefined,
    rowOffsetY: Array.isArray(g.rowOffsetY)
      ? g.rowOffsetY.map((v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0))
      : undefined,
    rowOffsetX: Array.isArray(g.rowOffsetX)
      ? g.rowOffsetX.map((v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0))
      : undefined,
  });
}

function clampMonsterStat(
  value: unknown,
  min: number,
  max: number,
): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function normalizePart(raw: unknown): HomePartDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<HomePartDefinition> & {
    cellsW?: number;
    cellsH?: number;
  };
  if (
    typeof p.id !== "string" ||
    typeof p.name !== "string" ||
    typeof p.imageDataUrl !== "string" ||
    !isCellCount(p.cellsW) ||
    !isCellCount(p.cellsH) ||
    typeof p.createdAt !== "number"
  ) {
    return null;
  }
  return createPartDefinition({
    id: p.id,
    name: p.name,
    imageDataUrl: p.imageDataUrl,
    cellsW: p.cellsW,
    cellsH: p.cellsH,
    kind: normalizePartKind(p.kind),
    footprintW: isCellCount(p.footprintW) ? p.footprintW : p.cellsW,
    footprintH: isCellCount(p.footprintH) ? p.footprintH : p.cellsH,
    tags: p.tags,
    depthBias: typeof p.depthBias === "number" ? p.depthBias : 0,
    placeScale: typeof p.placeScale === "number" ? p.placeScale : 1,
    passage: p.passage,
    zones: p.zones,
    sockets: p.sockets,
    createdAt: p.createdAt,
    monsterLevel: p.monsterLevel,
    monsterBaseHp: p.monsterBaseHp,
    monsterBaseAttack: p.monsterBaseAttack,
    monsterBaseDefense: p.monsterBaseDefense,
    monsterBaseDodge: p.monsterBaseDodge,
    monsterXpReward: p.monsterXpReward,
    spritesheetDataUrl: p.spritesheetDataUrl,
    grid: p.grid,
    sourceSheetDataUrl: p.sourceSheetDataUrl,
    gridTuning: p.gridTuning,
  });
}

export function normalizeHomeRoom(parsed: LegacyHomeRoom): HomeRoomData {
  const theme = themeById(parsed.themeId ?? "default");
  const parts = (parsed.parts ?? [])
    .map(normalizePart)
    .filter((p): p is HomePartDefinition => p !== null);
  const partIds = new Set(parts.map((p) => p.id));
  const partByIdMap = new Map(parts.map((p) => [p.id, p]));

  const rawCols = parsed.gridCols ?? HOME_GRID_COLS;
  const rawRows = parsed.gridRows ?? HOME_GRID_ROWS;
  const legacyDefault = HOME_GRID_LEGACY_SIZES.some(
    (size) => size.cols === rawCols && size.rows === rawRows,
  );
  const gridCols = legacyDefault ? HOME_GRID_COLS : rawCols;
  const gridRows = legacyDefault ? HOME_GRID_ROWS : rawRows;

  const placements = (parsed.placements ?? [])
    .filter((value): value is HomePlacement => {
      if (!isValidPlacement(value)) return false;
      if (!partIds.has(value.partId)) return false;
      const part = partByIdMap.get(value.partId);
      if (!part) return false;
      const fp = partFootprintForNormalize(part);
      return (
        value.gridX >= 0 &&
        value.gridY >= 0 &&
        value.gridX + fp.w <= gridCols &&
        value.gridY + fp.h <= gridRows
      );
    })
    .map(normalizePlacementRecord);

  let backdropPartId =
    typeof parsed.backdropPartId === "string" ? parsed.backdropPartId : null;
  if (backdropPartId && !partIds.has(backdropPartId)) backdropPartId = null;

  let floorPartId =
    typeof parsed.floorPartId === "string" ? parsed.floorPartId : null;
  if (floorPartId && !partIds.has(floorPartId)) floorPartId = null;

  const rawBackdropUrl =
    typeof parsed.backdropUrl === "string" && parsed.backdropUrl
      ? parsed.backdropUrl
      : null;
  const backdropUrl = migrateThemeBackdropUrl(rawBackdropUrl);

  let shellPartId = typeof parsed.shellPartId === "string" ? parsed.shellPartId : null;
  if (shellPartId && !partIds.has(shellPartId)) shellPartId = null;

  return {
    version: HOME_DATA_VERSION,
    gridCols,
    gridRows,
    parts,
    placements,
    themeId: theme.id,
    backdropPartId,
    backdropUrl,
    shellPartId,
    floorPartId,
  };
}

function partFootprintForNormalize(part: HomePartDefinition): { w: number; h: number } {
  return {
    w: part.footprintW || part.cellsW,
    h: part.footprintH || part.cellsH,
  };
}

function loadHomeRoomFromLocalStorage(userId: string): HomeRoomData | null {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as LegacyHomeRoom;
    if (!Array.isArray(parsed.parts) || !Array.isArray(parsed.placements)) {
      return null;
    }
    if (parsed.version !== 1 && parsed.version !== 2) {
      return null;
    }
    return normalizeHomeRoom(parsed);
  } catch {
    localStorage.removeItem(storageKey(userId));
    return null;
  }
}

/** Parse room JSON from IndexedDB (shared by home + creator scenes). */
export function homeRoomFromStored(raw: unknown): HomeRoomData | null {
  if (!raw || typeof raw !== "object") return null;
  const parsed = raw as LegacyHomeRoom;
  if (!Array.isArray(parsed.parts) || !Array.isArray(parsed.placements)) {
    return null;
  }
  if (parsed.version !== 1 && parsed.version !== 2) {
    return null;
  }
  return normalizeHomeRoom(parsed);
}

/** Load home layout + parts (IndexedDB; migrates legacy localStorage once). */
export async function loadHomeRoom(userId: string): Promise<HomeRoomData> {
  const legacy = loadHomeRoomFromLocalStorage(userId);
  if (legacy) {
    try {
      await saveHomeRoomToIdb(userId, legacy);
      localStorage.removeItem(storageKey(userId));
    } catch {
      return legacy;
    }
    return legacy;
  }

  const fromIdb = await loadHomeRoomFromIdb(userId);
  return homeRoomFromStored(fromIdb) ?? createDefaultHomeRoom();
}

/** Persist home data (IndexedDB — supports large sprite libraries). */
export async function saveHomeRoom(userId: string, data: HomeRoomData): Promise<void> {
  try {
    await saveHomeRoomToIdb(userId, normalizeHomeRoom(data));
    try {
      localStorage.removeItem(storageKey(userId));
    } catch {
      /* ignore */
    }
    const { schedulePlayerCloudPush } = await import("@/cloud/player-cloud-scheduler");
    schedulePlayerCloudPush(userId, "home");
  } catch (err) {
    if (isStorageQuotaError(err)) {
      throw new Error("พื้นที่เก็บข้อมูลเต็ม — ลบชิ้นที่ไม่ใช้ หรือสร้างทีละน้อย");
    }
    throw err;
  }
}

export { isStorageQuotaError };

function isValidPlacement(value: unknown): value is HomePlacement {
  if (!value || typeof value !== "object") return false;
  const p = value as HomePlacement;
  return (
    typeof p.id === "string" &&
    typeof p.partId === "string" &&
    Number.isInteger(p.gridX) &&
    Number.isInteger(p.gridY)
  );
}

export function normalizePlacementRotation(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  const stepped = Math.round(value / 90) * 90;
  return ((stepped % 360) + 360) % 360;
}

function normalizePlacementRecord(placement: HomePlacement): HomePlacement {
  const rotation = normalizePlacementRotation(placement.rotation);
  const out: HomePlacement = {
    id: placement.id,
    partId: placement.partId,
    gridX: placement.gridX,
    gridY: placement.gridY,
  };
  if (placement.depthBias !== undefined) {
    out.depthBias = clampPlacementDepthBias(placement.depthBias);
  }
  if (placement.flipX) out.flipX = true;
  if (placement.flipY) out.flipY = true;
  if (rotation !== 0) out.rotation = rotation;
  if (placement.placeScale !== undefined) {
    out.placeScale = clampPlaceScale(placement.placeScale);
  }
  return out;
}

function isCellCount(n: unknown): n is number {
  return (
    typeof n === "number" &&
    Number.isInteger(n) &&
    n >= HOME_PART_CELLS_MIN &&
    n <= HOME_PART_CELLS_MAX
  );
}

export function clampPartCells(n: number): number {
  return Math.min(HOME_PART_CELLS_MAX, Math.max(HOME_PART_CELLS_MIN, Math.floor(n)));
}

export const HOME_PLACE_SCALE_MIN = 0.25;
export const HOME_PLACE_SCALE_MAX = 3;
export const HOME_PLACE_SCALE_STEP = 0.1;

export function clampPlaceScale(n: number): number {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 1;
  return (
    Math.round(
      Math.min(HOME_PLACE_SCALE_MAX, Math.max(HOME_PLACE_SCALE_MIN, v)) * 100,
    ) / 100
  );
}

export function placeScalePercent(scale: number): number {
  return Math.round(clampPlaceScale(scale) * 100);
}

export function placeScaleFromPercent(percent: number): number {
  return clampPlaceScale(percent / 100);
}

export const HOME_PLACEMENT_DEPTH_BIAS_MIN = -48;
export const HOME_PLACEMENT_DEPTH_BIAS_MAX = 48;
export const HOME_DEPTH_BIAS_STEP = 2;

export function clampPlacementDepthBias(n: number): number {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  return Math.max(
    HOME_PLACEMENT_DEPTH_BIAS_MIN,
    Math.min(HOME_PLACEMENT_DEPTH_BIAS_MAX, Math.round(v)),
  );
}

export function newHomeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `home-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function countPlacementsOfKind(
  data: HomeRoomData,
  kind: HomePartKind,
): number {
  return data.placements.reduce((n, placement) => {
    const part = data.parts.find((p) => p.id === placement.partId);
    return part?.kind === kind ? n + 1 : n;
  }, 0);
}

export function kindQuotaCap(kind: HomePartKind): number {
  return kindPreset(kind).quota;
}

export function kindQuotaUsed(data: HomeRoomData, kind: HomePartKind): number {
  if (kind === "backdrop") {
    return data.backdropPartId ? 1 : 0;
  }
  if (kind === "shell") {
    return data.shellPartId ? 1 : 0;
  }
  return countPlacementsOfKind(data, kind);
}

export function kindQuotaRemaining(data: HomeRoomData, kind: HomePartKind): number {
  return Math.max(0, kindQuotaCap(kind) - kindQuotaUsed(data, kind));
}

export function resolveRoomBackdropUrl(data: HomeRoomData): string | null {
  if (data.backdropPartId) {
    const part = data.parts.find((p) => p.id === data.backdropPartId);
    if (part) return normalizeImageDataUrl(part.imageDataUrl);
  }
  return migrateThemeBackdropUrl(data.backdropUrl);
}

export function resolveStageShellUrl(data: HomeRoomData): string | null {
  if (!data.shellPartId) return null;
  const part = data.parts.find((p) => p.id === data.shellPartId);
  if (!part) return null;
  return normalizeImageDataUrl(part.imageDataUrl);
}

export function applyThemeToRoom(data: HomeRoomData, themeId: string): void {
  const theme = themeById(themeId);
  data.themeId = theme.id;
  data.backdropPartId = null;
  data.backdropUrl = null;
}

/** Resize room grid; drops placements that no longer fit. */
export function applyGridSizeToRoom(
  data: HomeRoomData,
  cols: number,
  rows: number,
): { cols: number; rows: number; removed: number } {
  const nextCols = clampGridCols(cols);
  const nextRows = clampGridRows(rows);
  const before = data.placements.length;
  data.gridCols = nextCols;
  data.gridRows = nextRows;
  data.placements = data.placements.filter((placement) => {
    const part = data.parts.find((p) => p.id === placement.partId);
    if (!part) return false;
    const w = part.footprintW || part.cellsW;
    const h = part.footprintH || part.cellsH;
    return (
      placement.gridX >= 0 &&
      placement.gridY >= 0 &&
      placement.gridX + w <= nextCols &&
      placement.gridY + h <= nextRows
    );
  });
  if (data.floorPartId) {
    const floor = data.parts.find((p) => p.id === data.floorPartId);
    if (floor && normalizePartKind(floor.kind) === "floor") {
      floor.cellsW = nextCols;
      floor.cellsH = nextRows;
      floor.footprintW = nextCols;
      floor.footprintH = nextRows;
    }
  }
  return { cols: nextCols, rows: nextRows, removed: before - data.placements.length };
}
